import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Input } from "@base-ui/react/input";
import { createOrganization } from "../../api/organizations";
import { createOrgSlug } from "../../lib/slug";
import cobbleLogo from "../../cobbleLogo.svg";

export function OnboardingScreen() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");

  const mutation = useMutation({
    mutationFn: (orgName: string) => createOrganization(getToken, orgName),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      const slug = createOrgSlug(data.id, name.trim());
      navigate({
        to: "/organization/$slug/overview",
        params: { slug },
      });
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-8 px-4">
        {/* Header */}
        <div className="flex flex-col items-center gap-3">
          <img src={cobbleLogo} alt="Cobble logo" className="h-12 w-12" />
          <h1 className="text-2xl font-semibold text-gray-900">
            Welcome to Cobble
          </h1>
          <p className="text-sm text-gray-500">
            Get started by creating or joining an organization.
          </p>
        </div>

        {/* Create organization card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-base font-medium text-gray-900">
            Create a new organization
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Start fresh with your own club or team.
          </p>
          <form
            className="mt-4 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) {
                mutation.mutate(name.trim());
              }
            }}
          >
            <Input
              placeholder="Organization name"
              value={name}
              onValueChange={setName}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              type="submit"
              disabled={!name.trim() || mutation.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-darker disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {mutation.isPending ? "Creating…" : "Create"}
            </button>
          </form>
          {mutation.isError && (
            <p className="mt-2 text-sm text-red-600">
              Failed to create organization. Please try again.
            </p>
          )}
        </div>

        {/* Join organization card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm opacity-60">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-medium text-gray-900">
                Join an existing organization
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Accept an invitation from your organization admin.
              </p>
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
              Coming soon
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
